import {
  Badge,
  Box,
  Chip,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu
} from '@material-ui/core';
import { lighten, makeStyles, withStyles } from '@material-ui/core/styles';
import { ImportExport, MoreVert } from '@material-ui/icons';
import {
  InfiniteScrollGrid, useBaseStore
} from '@roc/feature-app-core';
import { isActiveManualQuote, ManualQuoteStatus, PORTAL_COLORS, Utility } from '@roc/feature-utils';
import {
  LinkColumn, SelectFilterComponent,
  SelectFloatingFilterComponent, StatusChip,
  StatusType, SubmitMenuItem, TextColumn, FastForwardIcon, GridColumnProps
} from '@roc/ui';
import { observer } from 'mobx-react';
import React, { useState, useEffect } from 'react';
import { useLoanRoutes } from '@roc/feature-loans-routes-config';
import { useLoanStore } from '../hooks/useLoanStore';
import { useUserStore } from '@roc/feature-app-core';
import { useProposalsRoutes } from '@roc/feature-proposals';
import { formatCurrency, noOpComparator } from '@roc/ui/utils';
import { useManualQuoteStore } from '../hooks/useManualQuoteStore';
import { ManualQuoteDeclineDialog } from 'libs/feature-loan-details/src/loanDetails/components/bridge/manualQuoteDeclineDialog';

const usdAmount: any = params => {
  return params.value && formatCurrency(Number(params.value));
};

const columns: GridColumnProps[] = [
  {
    field: 'dealid',
    headerName: 'Loan Id',
    minWidth: 80,
    cellRenderer: 'loanIdCellRenderer',
    comparator: noOpComparator,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    pinned: 'left',
    lockPinned: true,
  },
  {
    field: 'dealName',
    headerName: 'Deal Name',
    minWidth: 275,
    cellRenderer: 'dealNameCellRenderer',
    comparator: noOpComparator,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
  },
  {
    field: 'manualQuoteStatus',
    headerName: 'Status',
    minWidth: 200,
    cellRenderer: 'statusCellRenderer',
    comparator: noOpComparator,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
  },
  {
    field: 'loanSubType',
    headerName: 'Loan Type',
    minWidth: 200,
    comparator: noOpComparator,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
  },
  {
    field: 'amount',
    headerName: 'Amount',
    minWidth: 130,
    valueFormatter: usdAmount,
    comparator: noOpComparator,
  },
  {
    field: 'submissionDate',
    headerName: 'Submission Date',
    minWidth: 185,
    comparator: noOpComparator,
    filter: 'agDateColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
  },
  {
    field: 'submittedBy',
    headerName: 'Submitted By',
    minWidth: 300,
    comparator: noOpComparator,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
  },
  {
    field: 'action',
    headerName: 'Action',
    minWidth: 80,
    cellRenderer: 'actionCellRenderer',
    comparator: noOpComparator,
    width: 80,
    pinned: 'right',
    lockPinned: true
  },
];

const StyledBadge = withStyles(theme => ({
  badge: {
    right: -12,
    top: 13,
    border: `2px solid ${theme.palette.background.paper}`,
    padding: '0 4px',
    fontSize: '11px',
    backgroundColor: PORTAL_COLORS.unreadMessages,
    color: 'white',
    fontWeight: 700,
    userSelect: 'none',
  },
}))(Badge);

const useStyles = makeStyles((theme) => ({
  greenRow: {
    '& .ag-cell': {
      backgroundColor: lighten(theme.palette.success.main, 0.85),
    },
  },
  redRow: {
    '& .ag-cell': {
      backgroundColor: lighten(theme.palette.error.main, 0.85),
    },
  },
  blueRow: {
    '& .ag-cell': {
      backgroundColor: lighten(theme.palette.info.main, 0.85),
    },
  },
}))

const getUnreadMessagesCount = unreadMessages => {
  const { userStore } = useUserStore();
  const allowLoanPricing = userStore.allowLoanPricing;

  if (unreadMessages && allowLoanPricing) {
    return unreadMessages > 99 ? '99+' : unreadMessages;
  }
  return undefined;
};

export const MyManualQuoteLoansGrid = observer(() => {
  const classes = useStyles();
  const { loanStore } = useLoanStore();
  const { loanRoutesConfig } = useLoanRoutes();
  const [loanId, setLoanId] = useState(0);
  const [anchorEl, setAnchorEl] = useState<HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const { userStore } = useUserStore();
  const allowLoanPricing = userStore.allowLoanPricing;
  const { proposalsRoutesConfig } = useProposalsRoutes();
  const [showDeclineManualQuote, setShowDeclineManualQuote] = useState(false);
  const { manualQuoteStore } = useManualQuoteStore();

  useEffect(() => {
    loanStore.fetchInternalInsuranceReviewers();
  }, []);

  const isDealRoom = (data) => {
    return Utility.PROPOSAL_INFORMATION === data?.utility;
  }

  const getUrl = (row) => {
    const loanId = row?.dealid;
    return !isDealRoom(row)
      ? loanRoutesConfig.loans(loanId).children.manualQuote.url
      : proposalsRoutesConfig.viewProposal(loanId).url;
  }

  const frameworkComponents = {
    selectFilter: SelectFilterComponent,
    selectFloatingFilter: SelectFloatingFilterComponent,
    loanIdCellRenderer: params =>
      loanStore.isLoanSubtypeSupported(params.node.data?.loanSubType) ? (
        <StyledBadge
          badgeContent={getUnreadMessagesCount(
            params.node.data?.unreadMessages
          )}
        >
          {LinkColumn({
            ...params,
            url: getUrl(params.node.data),
          })}
        </StyledBadge>
      ) : (
        <StyledBadge
          badgeContent={getUnreadMessagesCount(
            params.node.data?.unreadMessages
          )}
        >
          {TextColumn(params)}
        </StyledBadge>
      ),
    pendingDocumentsCellRenderer: ({ value }) => {
      if (!value) {
        return null;
      }
      return (
        <Box pt={1} display="flex" justifyContent="center">
          <Chip
            size="small"
            label={value}
            style={{
              backgroundColor: PORTAL_COLORS.pendingDocuments,
              color: 'white',
              fontWeight: 700,
            }}
          />
        </Box>
      );
    },
    fastTrackCellRenderer: ({ value }) => {
      if (!value) {
        return null;
      }
      return (<FastForwardIcon value={value} />)
    },
    appraisalReportUploadedCellRenderer: ({ value }) => {
      if (!value) {
        return null;
      }
      return (<Chip
        size="small"
        label={value ? "Yes" : "No"}
        style={{
          backgroundColor: '#89C053',
          color: 'white',
          fontWeight: 700,
        }}
      />)
    },
    appraisalPaidCellRenderer: ({ value }) => {
      if (!value) {
        return null;
      }
      return (<Chip
        size="small"
        label={value ? "Yes" : "No"}
        style={{
          backgroundColor: '#89C053',
          color: 'white',
          fontWeight: 700,
        }}
      />)
    },
    borrowerSubmissionCellRenderer: ({ value }) => {
      if (!value) {
        return null;
      }
      return (<Chip
        size="small"
        label={value ? "Yes" : "No"}
        style={{
          backgroundColor: '#89C053',
          color: 'white',
          fontWeight: 700,
        }}
      />)
    },
    dealNameCellRenderer: params => {
      return LinkColumn({
        ...params,
        url: getUrl(params.node.data),
      });
    },
    statusCellRenderer: ({ value }) =>
      value ? (
        <StatusChip label={value} size="small" statusType={StatusType.MANUAL_QUOTE} />
      ) : null,
    insuranceStatusCellRenderer: ({ value }) =>
      value ? (
        <StatusChip label={value} size="small" statusType={StatusType.INSURANCE_STATUS} />
      ) : null,
    preScreenStatusCellRenderer: ({ value }) =>
      value ? (
        <StatusChip label={value} size="small" statusType={StatusType.PRESCREEN} />
      ) : null,
    actionCellRenderer: params => {
      return (
        <IconButton onClick={e => openMenu(e, params.node.data)}>
          <MoreVert color="primary" />
        </IconButton>
      );
    },
  };

  const openMenu = (event: React.MouseEvent<HTMLElement>, record) => {
    const { dealid } = record;
    setLoanId(dealid);
    setAnchorEl(event.currentTarget);
    setShowDeclineManualQuote(isActiveManualQuote(record?.manualQuoteStatus));
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const checkLoanPricingFlag = (columns) => {
    if (!allowLoanPricing) {
      return columns.filter(c => c.field != "interestEconomics" && c.field != "keyRatios");
    } else {
      return columns;
    }
  }

  const getRowClass = (params) => {
    if (params.node.data?.manualQuoteStatus === ManualQuoteStatus.WAITING_ON_SALES) {
      return classes.blueRow
    }
    if (params.node.data?.manualQuoteStatus === ManualQuoteStatus.APPROVED) {
      return classes.greenRow
    }
    if (params.node.data?.manualQuoteStatus === ManualQuoteStatus.REJECTED) {
      return classes.redRow
    }
  }

  return (
    <>
      <InfiniteScrollGrid
        columns={checkLoanPricingFlag(columns)}
        frameworkComponents={frameworkComponents}
        store={loanStore.myManualQuoteLoansGridStore}
        getRowClass={getRowClass}
      />
      <Menu
        id="menu-appbar"
        getContentAnchorEl={null}
        anchorEl={anchorEl}
        keepMounted
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        open={menuOpen}
        onClose={handleMenuClose}
      >
        {showDeclineManualQuote &&
          <SubmitMenuItem
            testId={`decline-manual-quote-${loanId}`}
            onClick={async () => {
              const data = {
                loanId,
                source: 'cportal'
              };
              await manualQuoteStore.declineManualQuote(data);
              loanStore.myManualQuoteLoansGridStore.resetAndFetchGridData();
              handleMenuClose();
            }}
            submitDialogProps={{
              title: `Decline Manual Quote ${loanId}`,
              body: <ManualQuoteDeclineDialog />,
              okButtonText: 'Submit',
            }}
          >
            <ListItemIcon>
              <ImportExport fontSize="small" />
            </ListItemIcon>
            <ListItemText>Decline Manual Quote</ListItemText>
          </SubmitMenuItem>
        }
      </Menu>
    </>
  );
});
